import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import CronBuilder from './CronBuilder';
import ParameterEditor from './ParameterEditor';

const JobForm = ({ job, onClose }) => {
  const { getProtectedTenantCode } = useTenantProtection();
  const isEditing = !!job;

  const [formData, setFormData] = useState({
    jobName: '',
    description: '',
    endpointKey: '', // Changed from endpointId to endpointKey
    executionMode: 'scheduled',
    cronSchedule: '0 8 * * *',
    parameters: {},
    isEnabled: true,
  });

  const [endpoints, setEndpoints] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [alertConfig, setAlertConfig] = useState({
    alertOnSuccess: false,
    alertOnFailure: true,
    alertMethod: 'email',
    emailRecipients: '',
    lineRecipients: '',
    customMessage: '',
  });
  const [loading, setLoading] = useState(false);
  const [loadingEndpoints, setLoadingEndpoints] = useState(true);

  useEffect(() => {
    loadEndpoints();
    if (job) {
      loadJobData();
      loadAlertConfig();
    }
  }, [job]);

  const loadEndpoints = async () => {
    try {
      setLoadingEndpoints(true);
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get('/api/automation/endpoints', {
        params: { isActive: true },
        headers: { 'X-Tenant-Code': tenantCode },
      });
      setEndpoints(response.data);
    } catch (error) {
      console.error('Failed to load endpoints:', error);
      toast.error('Failed to load endpoints');
    } finally {
      setLoadingEndpoints(false);
    }
  };

  const loadJobData = () => {
    setFormData({
      jobName: job.JobName,
      description: job.Description || '',
      endpointKey: job.EndpointKey || job.EndpointID, // Try EndpointKey first, fallback to EndpointID
      executionMode: job.ExecutionMode,
      cronSchedule: job.CronSchedule || '0 8 * * *',
      parameters: job.JobParameters ? JSON.parse(job.JobParameters) : {},
      isEnabled: job.IsEnabled,
    });
  };

  const loadAlertConfig = async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await axios.get(`/api/automation/jobs/${job.JobID}/alert-config`, {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      if (response.data && response.data.AlertConfigID) {
        setAlertConfig({
          alertOnSuccess: response.data.AlertOnSuccess,
          alertOnFailure: response.data.AlertOnFailure,
          alertMethod: response.data.AlertMethod || 'email',
          emailRecipients: response.data.EmailRecipients || '',
          lineRecipients: response.data.LineRecipients || '',
          customMessage: response.data.CustomMessage || '',
        });
      }
    } catch (error) {
      console.error('Failed to load alert config:', error);
    }
  };

  useEffect(() => {
    if (formData.endpointKey) {
      // Match by EndpointKey (string) instead of EndpointID (number)
      const endpoint = endpoints.find(e => e.EndpointKey === formData.endpointKey || e.EndpointID === formData.endpointKey);
      console.log('Selected endpoint:', endpoint);
      console.log('Endpoint has ParameterDefinitions:', endpoint?.ParameterDefinitions);
      console.log('Endpoint has Parameters:', endpoint?.Parameters);
      console.log('Endpoint has RequiredParameters:', endpoint?.RequiredParameters);
      setSelectedEndpoint(endpoint);
    }
  }, [formData.endpointKey, endpoints]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.jobName.trim()) {
      toast.error('Job name is required');
      return;
    }
    if (!formData.endpointKey) {
      toast.error('Please select an endpoint');
      return;
    }
    if (formData.executionMode === 'scheduled' && !formData.cronSchedule) {
      toast.error('Cron schedule is required for scheduled jobs');
      return;
    }

    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();

      const jobPayload = {
        ...formData,
        endpointKey: formData.endpointKey, // Send as string, not parsed as integer
      };

      let jobId;
      if (isEditing) {
        await axios.put(
          `/api/automation/jobs/${job.JobID}`,
          jobPayload,
          { headers: { 'X-Tenant-Code': tenantCode } }
        );
        jobId = job.JobID;
        toast.success('Job updated successfully');
      } else {
        const response = await axios.post(
          '/api/automation/jobs',
          jobPayload,
          { headers: { 'X-Tenant-Code': tenantCode } }
        );
        jobId = response.data.JobID;
        toast.success('Job created successfully');
      }

      // Save alert configuration
      await axios.post(
        `/api/automation/jobs/${jobId}/alert-config`,
        alertConfig,
        { headers: { 'X-Tenant-Code': tenantCode } }
      );

      onClose();
    } catch (error) {
      console.error('Failed to save job:', error);
      toast.error(error.response?.data?.error || 'Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Edit Job' : 'Create New Job'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-6">
            {/* Basic Information */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Job Name *
                  </label>
                  <input
                    type="text"
                    value={formData.jobName}
                    onChange={(e) => setFormData({ ...formData, jobName: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    placeholder="My Automation Job"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    placeholder="What does this job do?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Endpoint *
                  </label>
                  <select
                    value={formData.endpointKey}
                    onChange={(e) => setFormData({ ...formData, endpointKey: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    disabled={loadingEndpoints}
                  >
                    <option value="">Select an endpoint...</option>
                    {endpoints.map((endpoint) => (
                      <option key={endpoint.EndpointKey || endpoint.EndpointID} value={endpoint.EndpointKey || endpoint.EndpointID}>
                        {endpoint.EndpointName} {endpoint.IsSystemEndpoint ? '(System)' : '(Custom)'}
                      </option>
                    ))}
                  </select>
                  {selectedEndpoint && (
                    <p className="mt-1 text-sm text-gray-500">
                      {selectedEndpoint.Description}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Execution Mode */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Execution Settings</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Execution Mode *
                  </label>
                  <select
                    value={formData.executionMode}
                    onChange={(e) => setFormData({ ...formData, executionMode: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                  >
                    <option value="scheduled">Scheduled (Cron)</option>
                    <option value="manual">Manual Only</option>
                    <option value="api">API Triggered</option>
                    <option value="all">All Methods</option>
                  </select>
                </div>

                {(formData.executionMode === 'scheduled' || formData.executionMode === 'all') && (
                  <CronBuilder
                    value={formData.cronSchedule}
                    onChange={(value) => setFormData({ ...formData, cronSchedule: value })}
                  />
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isEnabled}
                    onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Enable this job
                  </label>
                </div>
              </div>
            </div>

            {/* Parameters */}
            {selectedEndpoint && (selectedEndpoint.ParameterDefinitions || selectedEndpoint.RequiredParameters || selectedEndpoint.Parameters) && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Parameters</h4>
                <ParameterEditor
                  schema={selectedEndpoint.ParameterDefinitions || selectedEndpoint.RequiredParameters || selectedEndpoint.Parameters}
                  values={formData.parameters}
                  onChange={(values) => setFormData({ ...formData, parameters: values })}
                />
              </div>
            )}

            {/* Alert Configuration */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Alert Configuration</h4>
              <div className="space-y-4">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={alertConfig.alertOnSuccess}
                      onChange={(e) => setAlertConfig({ ...alertConfig, alertOnSuccess: e.target.checked })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Alert on success
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={alertConfig.alertOnFailure}
                      onChange={(e) => setAlertConfig({ ...alertConfig, alertOnFailure: e.target.checked })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Alert on failure
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Alert Method
                  </label>
                  <select
                    value={alertConfig.alertMethod}
                    onChange={(e) => setAlertConfig({ ...alertConfig, alertMethod: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                  >
                    <option value="email">Email</option>
                    <option value="line">LINE</option>
                    <option value="both">Both</option>
                  </select>
                </div>

                {(alertConfig.alertMethod === 'email' || alertConfig.alertMethod === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email Recipients (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={alertConfig.emailRecipients}
                      onChange={(e) => setAlertConfig({ ...alertConfig, emailRecipients: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      placeholder="admin@example.com, manager@example.com"
                    />
                  </div>
                )}

                {(alertConfig.alertMethod === 'line' || alertConfig.alertMethod === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      LINE Recipients (User IDs, comma-separated)
                    </label>
                    <input
                      type="text"
                      value={alertConfig.lineRecipients}
                      onChange={(e) => setAlertConfig({ ...alertConfig, lineRecipients: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                      placeholder="Uxxxxxxxxxxxxx, Uyyyyyyyyyyyyy"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Custom Message
                  </label>
                  <textarea
                    value={alertConfig.customMessage}
                    onChange={(e) => setAlertConfig({ ...alertConfig, customMessage: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                    placeholder="Additional message to include in alerts"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : isEditing ? 'Update Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobForm;

